import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Card, CardContent, Rating, Skeleton, Typography } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import {
  studentFormsApi,
  type StudentForm,
  type StudentFormAnswerValue,
  type StudentFormField,
} from '@/api/studentForms'
import { formatDateDMY } from '@/utils/date'
import { StudentFormEditDialog } from './StudentFormEditDialog'

interface StudentFormSummaryProps {
  contractId: number
  /** Учитель ведёт анкету: правит все поля и смотрит историю правок. */
  canManage: boolean
  /** Отступ сверху: блок встраивается в разные страницы. */
  sx?: object
}

/**
 * Анкета ученика отдельным блоком в профиле — только заполненные поля.
 *
 * Пустые поля не показываются: блок отвечает на вопрос «что известно об
 * ученике», а не «что осталось заполнить». Полный список и правка —
 * в модальном окне, как у договора.
 */
export function StudentFormSummary({ contractId, canManage, sx }: StudentFormSummaryProps) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [form, setForm] = useState<StudentForm | null>(null)
  const [editOpen, setEditOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setFailed(false)

    void (async () => {
      try {
        const data = await studentFormsApi.getForm(contractId)
        if (!cancelled) setForm(data)
      } catch {
        // Анкета — дополнительный блок: ошибка загрузки не ломает профиль.
        // Но и «не заполнена» показывать нельзя — учитель решит, что данных нет.
        if (!cancelled) {
          setForm(null)
          setFailed(true)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [contractId])

  const filled = useMemo(() => {
    if (!form) return []
    const byKey = new Map(form.fields.map((field) => [field.key, field]))

    return form.answers
      .map((answer) => ({ field: byKey.get(answer.field_key), value: answer.value }))
      .filter(
        (row): row is { field: StudentFormField; value: StudentFormAnswerValue } =>
          row.field !== undefined && !isEmptyValue(row.value),
      )
      .sort((a, b) => a.field.sort_order - b.field.sort_order)
  }, [form])

  // Ученику кнопка правки нужна, только если ему есть что заполнять.
  const canEdit =
    !failed &&
    (canManage || (form?.fields ?? []).some((field) => field.access === 'student_editable'))

  return (
    <Card sx={{ mt: 3, ...sx }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('studentForms.title')}</Typography>
          {canEdit && !loading && (
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditOpen(true)}
            >
              {filled.length > 0 ? t('common.edit', 'Редактировать') : t('studentForms.fill')}
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
          </Box>
        ) : failed ? (
          <Typography variant="body2" color="text.secondary">
            {t('studentForms.loadError')}
          </Typography>
        ) : filled.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('studentForms.summaryEmpty')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filled.map(({ field, value }) => (
              <Box key={field.key}>
                <Typography variant="body2" color="text.secondary">
                  {field.label}
                </Typography>
                {field.type === 'rating' && typeof value === 'number' ? (
                  <Rating value={value} readOnly size="small" />
                ) : (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {formatValue(field, value, {
                      yes: t('common.yes', 'Да'),
                      no: t('common.no', 'Нет'),
                    })}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      <StudentFormEditDialog
        open={editOpen}
        contractId={contractId}
        form={form}
        canManage={canManage}
        onClose={() => setEditOpen(false)}
        onSaved={(next) => {
          setForm(next)
          setEditOpen(false)
        }}
      />
    </Card>
  )
}

/** Незаполненное поле: null, пустая строка, пустой список, пустая пара языков. */
function isEmptyValue(value: StudentFormAnswerValue): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') {
    const pair = value as { from?: string; to?: string }
    return !pair.from?.trim() && !pair.to?.trim()
  }
  return false
}

function formatValue(
  field: StudentFormField,
  value: StudentFormAnswerValue,
  booleanLabels: { yes: string; no: string },
): string {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') {
    return value ? booleanLabels.yes : booleanLabels.no
  }
  if (typeof value === 'object' && value !== null) {
    const pair = value as { from?: string; to?: string }
    return [pair.from, pair.to].filter(Boolean).join(' → ')
  }
  if (field.type === 'date' && typeof value === 'string') return formatDateDMY(value)
  return String(value)
}
